import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

const EDGE = 24;
const SLOP = 10;
const SETTLE = "transform 220ms cubic-bezier(.2,.7,.2,1), opacity 220ms cubic-bezier(.2,.7,.2,1)";
const set = (el, s) => el && Object.assign(el.style, s);

// Phones and tablets: slide right from the left edge to pull the side menu out (it follows the finger),
// slide it back left to close it. Swipes that start away from the edge are left alone.
export function useSwipeMenu(media) {
  const [open, setRawOpen] = useState(false);
  const content = useRef(null);
  const overlay = useRef(null);
  const gesture = useRef(null);
  const pending = useRef(null);
  const openRef = useRef(open);
  openRef.current = open;

  const width = () => content.current?.offsetWidth || Math.min(window.innerWidth * 0.86, 300);
  const paint = useCallback((out) => {
    const w = width();
    set(content.current, { animation: "none", transition: "none", transform: `translateX(${Math.min(0, out - w)}px)` });
    set(overlay.current, { animation: "none", transition: "none", opacity: String(Math.max(0, Math.min(1, out / w))) });
  }, []);
  // Hands the menu back to its normal open and close animations.
  const release = () => [content.current, overlay.current].forEach((el) => set(el, { animation: "", transition: "", transform: "", opacity: "" }));

  const setOpen = useCallback((v) => {
    if (!v) release();
    setRawOpen(v);
  }, []);
  const contentRef = useCallback((el) => { content.current = el; if (el && pending.current !== null) paint(pending.current); }, [paint]);
  const overlayRef = useCallback((el) => { overlay.current = el; if (el && pending.current !== null) paint(pending.current); }, [paint]);

  useEffect(() => {
    const mq = window.matchMedia(media);
    const start = (e) => {
      if (!mq.matches || e.touches.length !== 1) return;
      const t = e.touches[0];
      const closing = openRef.current;
      if (closing ? !content.current?.contains(e.target) : t.clientX > EDGE || document.querySelector('[role="dialog"][data-state="open"]')) return;
      gesture.current = { closing, x0: t.clientX, y0: t.clientY, x: t.clientX, t: e.timeStamp, vx: 0, active: false };
    };
    const move = (e) => {
      const g = gesture.current;
      if (!g) return;
      const t = e.touches[0];
      const dx = t.clientX - g.x0;
      const dy = t.clientY - g.y0;
      if (!g.active) {
        if (Math.abs(dy) > SLOP && Math.abs(dy) > Math.abs(dx)) { gesture.current = null; return; }
        if (Math.abs(dx) < SLOP) return;
        if (g.closing ? dx > 0 : dx < 0) { gesture.current = null; return; }
        g.active = true;
        if (!g.closing) { pending.current = t.clientX; setRawOpen(true); }
      }
      e.preventDefault();
      g.vx = (t.clientX - g.x) / Math.max(1, e.timeStamp - g.t);
      g.x = t.clientX;
      g.t = e.timeStamp;
      g.out = g.closing ? width() + dx : t.clientX;
      if (!g.closing) pending.current = g.out;
      paint(g.out);
    };
    const end = () => {
      const g = gesture.current;
      gesture.current = null;
      pending.current = null;
      if (!g?.active) return;
      const w = width();
      const keep = g.vx > 0.3 || (g.vx > -0.3 && g.out > w * (g.closing ? 0.6 : 0.4));
      if (keep) {
        set(content.current, { transition: SETTLE, transform: "translateX(0)" });
        set(overlay.current, { transition: SETTLE, opacity: "1" });
        setTimeout(() => [content.current, overlay.current].forEach((el) => set(el, { transition: "", transform: "", opacity: "" })), 240);
        return;
      }
      // The closing slide carries on from where the finger let go.
      release();
      flushSync(() => setRawOpen(false));
    };
    document.addEventListener("touchstart", start, { passive: true });
    document.addEventListener("touchmove", move, { passive: false });
    document.addEventListener("touchend", end);
    document.addEventListener("touchcancel", end);
    return () => {
      document.removeEventListener("touchstart", start);
      document.removeEventListener("touchmove", move);
      document.removeEventListener("touchend", end);
      document.removeEventListener("touchcancel", end);
    };
  }, [media, paint]);

  return { open, setOpen, contentRef, overlayRef };
}
